import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { bffClient } from '@sluitstraat/core';
import {
  mapToCalculationRequest,
  mapFromCalculationResponse,
  cleanUnselectedCoveragePremiums,
  applyInboedelDiscount,
} from './calc';
import { validateInboedel } from './validation';
import { EC_CODE_MOBIEL, EC_CODE_SMARTHOME, getEc, selectedEcCode } from './coverages';
import { useDeferredCommit } from '../../../hooks/useDeferredCommit';

const calcInputFields = (data: any) => {
  const ba = data?.Contractdocument?.PP?.BA;
  const ip = data?.Contractdocument?.PP?.IP;
  const ib = data?.Contractdocument?.PP?.IB?.IB_INBVST !== undefined
    ? data.Contractdocument.PP.IB
    : { IB_INBVST: data?._uiState?.IB_INBVST };
  const dekkingen = data?.Supportsection?.Dekkingen || data?._uiState?.Dekkingen;
  return {
    BA_PCODE: String(ba?.BA_PCODE ?? ''),
    BA_HUISNR: String(ba?.BA_HUISNR ?? ''),
    BA_STRAAT: String(ba?.BA_STRAAT ?? ''),
    BA_PLAATS: String(ba?.BA_PLAATS ?? ''),
    BA_TYPEWON: String(ba?.BA_TYPEWON ?? ''),
    BA_AARDBW: String(ba?.BA_AARDBW ?? ''),
    BA_AARDDAK: String(ba?.BA_AARDDAK ?? ''),
    BA_WONING: String(ba?.BA_WONING ?? ''),
    BA_EIGENWO: String(ba?.BA_EIGENWO ?? ''),
    BA_PERMBEW: String(ba?.BA_PERMBEW ?? ''),
    BA_BDRFBEL: String(ba?.BA_BDRFBEL ?? ''),
    BA_OPPWON: String(ba?.BA_OPPWON ?? ''),
    // The herbouwwaarde is a calculation INPUT: it is what the Midglas engine rates glass
    // over, and its presence decides whether PP.GP may be sent at all. Leaving it out of the
    // signature meant typing one never re-priced, so the Glas card stayed blank forever.
    BA_HERBWRD: String(ba?.BA_HERBWRD ?? data?._uiState?.BA_HERBWRD ?? ''),
    IP_GSS: String(ip?.IP_GSS ?? ''),
    IP_VERZSOM: String(ip?.IP_VERZSOM ?? ''),
    IP_ERB: String(ip?.IP_ERB ?? ''),
    IB_INBVST: String(ib?.IB_INBVST ?? ''),
    // Smart Home REPLACES the Mobiele elektronica row on the wire (schema: one EC row), so the
    // toggle and its sum change what is rated, not only what is counted.
    Smarthome: String(dekkingen?.Smarthome ?? ''),
    EC_SMARTHOME_VERZSOM: String(getEc(data?.Contractdocument?.PP, EC_CODE_SMARTHOME)?.EC_VERZSOM ?? ''),
  };
};

/** What a calculation is keyed on: any change re-prices. */
export function inboedelCalcInputSignature(data: any): string {
  return JSON.stringify(calcInputFields(data));
}

/**
 * What the preview-price cache is keyed on: the calc inputs WITHOUT the Smart Home toggle and
 * sum. The cached badges (LS/OP/HU/KB, the EC rows) do not depend on the toggle, so flipping it
 * keeps them on screen while the re-pricing is in flight.
 */
export function inboedelPreviewCacheKey(data: any): string {
  const { Smarthome: _toggle, EC_SMARTHOME_VERZSOM: _sum, ...rest } = calcInputFields(data);
  return JSON.stringify(rest);
}

const COVERAGE_PRICES_CACHE_KEY = 'inboedel_coverage_prices';

export interface InboedelPreviewPrices {
  sections: Record<string, any>;
  ec: any[];
}

export const readCoveragePricesCache = (): ({ hash: string } & InboedelPreviewPrices) | null => {
  try {
    const stored = sessionStorage.getItem(COVERAGE_PRICES_CACHE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const restampCoveragePricesCache = (hash: string) => {
  const cache = readCoveragePricesCache();
  if (cache && cache.hash !== hash) {
    try {
      sessionStorage.setItem(COVERAGE_PRICES_CACHE_KEY, JSON.stringify({ ...cache, hash }));
    } catch {
      // Quota exceeded: the cache is a display optimization only.
    }
  }
};

const writeCoveragePricesCache = (hash: string, pp: any, extraEc: any[] = []) => {
  try {
    const sections: Record<string, any> = {};
    for (const key of ['LS', 'OP', 'HU', 'KB']) {
      if (pp?.[key]) sections[key] = pp[key];
    }
    const ec = [...(Array.isArray(pp?.EC) ? pp.EC : []), ...extraEc];
    sessionStorage.setItem(COVERAGE_PRICES_CACHE_KEY, JSON.stringify({ hash, sections, ec }));
  } catch {
    // Quota exceeded: the cache is a display optimization only.
  }
};

interface CoverageCalculationArgs {
  screenId: string;
  data: any;
  setData: (updater: any) => void;
  saveWorkflowData: (stepId: string, displayData: any) => void;
  t: (key: string, opts?: any) => string;
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  loading: boolean;
  isLoadingPropertyInfo: boolean;
  isMonumentProperty: boolean;
  isRentedProperty: boolean;
  isAddressLocked: boolean;
  isBuildingDetailsLocked: boolean;
  isGezinsamenstellingLocked: boolean;
  isGlassRebuildValueLocked: boolean;
  inboedelDiscountRef: React.MutableRefObject<{ enabled: boolean; percentage: number; excludeCoverages: string[]; applyToData: boolean }>;
  undiscountedDataRef: React.MutableRefObject<any>;
  glassDisabledRef: React.MutableRefObject<boolean>;
  onCalculationSuccess?: () => void;
}

export function useCoverageCalculation({
  screenId,
  data,
  setData,
  saveWorkflowData,
  t,
  setValidationErrors,
  loading,
  isLoadingPropertyInfo,
  isMonumentProperty,
  isRentedProperty,
  isAddressLocked,
  isBuildingDetailsLocked,
  isGezinsamenstellingLocked,
  isGlassRebuildValueLocked,
  inboedelDiscountRef,
  undiscountedDataRef,
  glassDisabledRef,
  onCalculationSuccess,
}: CoverageCalculationArgs) {
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [calculationSuccess, setCalculationSuccess] = useState(false);
  const { stage: stageSave, schedule: scheduleSave } = useDeferredCommit();
  const [lastCalculationInput, setLastCalculationInput] = useState<any>(null);
  const [lastCalculationOutput, setLastCalculationOutput] = useState<any>(null);
  const [shouldAutoCalculate, setShouldAutoCalculate] = useState(false);

  const initialLoadComplete = useRef(false);
  const autoCalculationTimer = useRef<number | null>(null);
  const isUpdatingFromCalculation = useRef(false);
  const lastCalculatedData = useRef<string>('');
  // Once the engine has shown it cannot price the Smart Home row (5470) on its own, stop asking:
  // every later recalculation goes straight to the paired request, so the badge costs two
  // round-trips (main + probe, in parallel) instead of three. The warning goes with it, so it is
  // one signal per session instead of one per recalculation.
  const smartHomeNeedsPairedProbe = useRef(false);

  const previewCacheKey = inboedelPreviewCacheKey(data);
  const previewPrices: InboedelPreviewPrices | null = useMemo(() => {
    const cache = readCoveragePricesCache();
    return cache && cache.hash === previewCacheKey ? { sections: cache.sections, ec: cache.ec } : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- isCalculating is the deliberate re-read trigger after a calculation rewrote the cache
  }, [previewCacheKey, isCalculating]);

  const getValidationErrors = useCallback((): Record<string, string> => {
    const entries = validateInboedel(data, {
      now: new Date(),
      isAddressLocked,
      isBuildingDetailsLocked,
      isGezinsamenstellingLocked,
      isGlassRebuildValueLocked,
      glassAllowed: !glassDisabledRef.current,
    });
    return Object.fromEntries(
      Object.entries(entries).map(([field, e]) => [field, t(e.key, e.params as any) as string])
    );
  }, [data, t, isAddressLocked, isBuildingDetailsLocked, isGezinsamenstellingLocked, isGlassRebuildValueLocked, glassDisabledRef]);

  const handleCalculate = useCallback(async (dataToCalculate?: any) => {
    let calculationData = dataToCalculate || data;

    // Check if address is validated before calculating
    // Skip when address is locked from Woonhuis - it was already validated upstream
    const ba = calculationData?.Contractdocument?.PP?.BA;
    if (!isAddressLocked && (!ba?.BA_STRAAT || !ba?.BA_PLAATS)) {
      setCalculationError(t('common.addressInvalid'));
      setValidationErrors(prev => ({
        ...prev,
        'Contractdocument.PP.BA.BA_STRAAT': t('screens.inboedel.validation.addressNotValidated')
      }));
      return;
    }

    // VALIDATION: Check for coverage amount limits before calculation
    const lsVerzsom = calculationData?.Contractdocument?.PP?.LS?.LS_VERZSOM || 0;
    const biVerzsom = calculationData?.Contractdocument?.PP?.BI?.BI_VERZSOM || 0;
    const huVerzsom = calculationData?.Contractdocument?.PP?.HU?.HU_VERZSOM || 0;
    const kbVerzsom = calculationData?.Contractdocument?.PP?.KB?.KB_VERZSOM || 0;

    const dekkingen = calculationData?.Supportsection?.Dekkingen || {};

    // Block calculation if jewelry out of range
    if (dekkingen.Lijfsieraden === 'J') {
      if (lsVerzsom < 15001) {
        setValidationErrors(prev => ({ ...prev, 'Contractdocument.PP.LS.LS_VERZSOM': t('screens.inboedel.validation.jewelryMinAmount') }));
        setIsCalculating(false);
        return;
      }
      if (lsVerzsom > 50000) {
        setValidationErrors(prev => ({ ...prev, 'Contractdocument.PP.LS.LS_VERZSOM': t('screens.inboedel.validation.jewelryMaxAmount') }));
        setIsCalculating(false);
        return;
      }
    }

    // Block calculation if outside home value not valid (only 7500 or 15000 allowed)
    if (dekkingen.Buitenshuis === 'J') {
      if (![7500, 15000].includes(biVerzsom)) {
        setValidationErrors(prev => ({ ...prev, 'Contractdocument.PP.BI.BI_VERZSOM': t('screens.inboedel.validation.outbuildingChooseAmount') }));
        setIsCalculating(false);
        return;
      }
    }

    // Block calculation if owner-tenant interest out of range
    if (dekkingen.Huurdersbelang === 'J') {
      if (huVerzsom < 15001) {
        setValidationErrors(prev => ({ ...prev, 'Contractdocument.PP.HU.HU_VERZSOM': t('screens.inboedel.validation.ownerTenantMinAmount') }));
        setIsCalculating(false);
        return;
      }
      if (huVerzsom > 50000) {
        setValidationErrors(prev => ({ ...prev, 'Contractdocument.PP.HU.HU_VERZSOM': t('screens.inboedel.validation.genericMaxAmount') }));
        setIsCalculating(false);
        return;
      }
    }

    // Block calculation if special possessions out of range
    if (dekkingen.Bijzonderebezittingen === 'J') {
      if (kbVerzsom < 15001) {
        setValidationErrors(prev => ({ ...prev, 'Contractdocument.PP.KB.KB_VERZSOM': t('screens.inboedel.validation.specialMinAmount') }));
        setIsCalculating(false);
        return;
      }
      if (kbVerzsom > 50000) {
        setValidationErrors(prev => ({ ...prev, 'Contractdocument.PP.KB.KB_VERZSOM': t('screens.inboedel.validation.specialMaxAmount') }));
        setIsCalculating(false);
        return;
      }
    }

    // ENSURE PPRC fields are ALWAYS present before calculation
    calculationData = {
      ...calculationData,
      Contractdocument: {
        ...calculationData.Contractdocument,
        PP: {
          ...calculationData.Contractdocument?.PP,
          PP_PPRC: calculationData.Contractdocument?.PP?.PP_PPRC ?? 20,
          IP: {
            ...calculationData.Contractdocument?.PP?.IP,
            IP_PPRC: calculationData.Contractdocument?.PP?.IP?.IP_PPRC ?? 20,
          },
          BI: {
            ...calculationData.Contractdocument?.PP?.BI,
            BI_PPRC: calculationData.Contractdocument?.PP?.BI?.BI_PPRC ?? 20,
          },
          GP: {
            ...calculationData.Contractdocument?.PP?.GP,
            GP_PPRC: calculationData.Contractdocument?.PP?.GP?.GP_PPRC ?? 20,
          },
        },
      },
    };

    try {
      setIsCalculating(true);
      setCalculationError(null);
      setCalculationSuccess(false);


      // Use mapping function to build request (handles conditional GP)
      const requestBody = mapToCalculationRequest(calculationData, { glassDisabled: glassDisabledRef.current });
      // The wire carries ONE EC row (Smart Home replaces Mobiele elektronica), so the Smart
      // Home badge needs its own pricing call while the toggle is off; with it on, the main
      // response already carries the 5470 row and the 5450 price is not displayed.
      const ecDekkingen = calculationData?.Supportsection?.Dekkingen || calculationData?._uiState?.Dekkingen;
      const probeEcCode = smartHomeNeedsPairedProbe.current
        ? [EC_CODE_MOBIEL, EC_CODE_SMARTHOME]
        : EC_CODE_SMARTHOME;
      const smartHomeProbe = selectedEcCode(ecDekkingen) === EC_CODE_SMARTHOME
        ? null
        : mapToCalculationRequest(calculationData, { glassDisabled: glassDisabledRef.current, ecCode: probeEcCode });

      // Store input (the wrapped request)
      setLastCalculationInput(requestBody);

      const calculate = (body: any) => bffClient.request<any>('POST', '/api/v1/productservice/calculate', body);
      const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));
      const [result, probe] = await Promise.all([
        calculate(requestBody),
        smartHomeProbe
          ? calculate(smartHomeProbe).then((r) => ({ response: r, error: null as string | null })).catch((e) => ({ response: null, error: errorText(e) }))
          : Promise.resolve(null),
      ]);

      // Smart Home price for the badge while the toggle is off. Shown in the debug panel as
      // _smartHomeProbe. When the engine does not price the 5470 row on its own, price it beside
      // the electronics row (the request it accepted before the one-row rule) and read only the
      // 5470 row from that answer. That paired request is the shape the one-row schema forbids,
      // so it is a bridge until the engine can price 5470 standalone — the warning below is the
      // signal that this needs a word with the engine team, logged ONCE per session because from
      // then on the probe is sent paired straight away.
      //
      // The latch requires an ANSWER (probe.response), not merely a missing row: a transient 500 or
      // a dropped connection also leaves smartHomeRow undefined, and latching on that would send
      // the schema-invalid paired request for the rest of the session over a network blip. With the
      // answer required, the latch means "the engine replied and had no 5470 row"; a failed probe
      // just costs that one badge update and is retried on the next recalculation.
      let smartHomeRow = probe?.response ? getEc(probe.response?.output?.Contractdocument?.PP, EC_CODE_SMARTHOME) : undefined;
      const smartHomeProbeInfo: any = probe ? { request: smartHomeProbe, response: probe.response ?? { error: probe.error } } : null;
      if (probe?.response && !smartHomeRow && !smartHomeNeedsPairedProbe.current) {
        smartHomeNeedsPairedProbe.current = true;
        const fallbackRequest = mapToCalculationRequest(calculationData, { glassDisabled: glassDisabledRef.current, ecCode: [EC_CODE_MOBIEL, EC_CODE_SMARTHOME] });
        try {
          const fallback = await calculate(fallbackRequest);
          smartHomeRow = getEc(fallback?.output?.Contractdocument?.PP, EC_CODE_SMARTHOME);
          smartHomeProbeInfo.fallback = { request: fallbackRequest, response: fallback };
        } catch (e) {
          smartHomeProbeInfo.fallback = { request: fallbackRequest, response: { error: errorText(e) } };
        }
        console.warn('[Inboedel] Smart Home (EC 5470) could not be priced on its own; pricing it beside the electronics row for the rest of this session.', smartHomeProbeInfo);
      }

      // Store raw BFF response for debug panel (always undiscounted)
      setLastCalculationOutput({ ...JSON.parse(JSON.stringify(result)), ...(smartHomeProbeInfo ? { _smartHomeProbe: smartHomeProbeInfo } : {}) });

      if (result && result.output) {
        // Use mapping function to process response (clears unselected premiums)
        const mappedData = mapFromCalculationResponse(result, calculationData);

        // Keep undiscounted copy for workflow persistence (when applyToData is false)
        const undiscountedMappedData = JSON.parse(JSON.stringify(mappedData));

        // Store undiscounted ref only when discount is active and applyToData is false
        if (inboedelDiscountRef.current.enabled && !inboedelDiscountRef.current.applyToData) {
          undiscountedDataRef.current = { Contractdocument: { PP: JSON.parse(JSON.stringify(mappedData?.Contractdocument?.PP || {})) } };
        } else {
          undiscountedDataRef.current = null;
        }

        applyInboedelDiscount(mappedData?.Contractdocument?.PP, inboedelDiscountRef.current);

        const smartHomePreview = smartHomeRow ? { EC: [JSON.parse(JSON.stringify(smartHomeRow))] } : null;
        if (smartHomePreview) applyInboedelDiscount(smartHomePreview, inboedelDiscountRef.current);
        writeCoveragePricesCache(inboedelPreviewCacheKey(mappedData), mappedData?.Contractdocument?.PP, smartHomePreview?.EC ?? []);

        // Set flag to prevent auto-calculation loop
        isUpdatingFromCalculation.current = true;
        const applyToData = !inboedelDiscountRef.current.enabled || inboedelDiscountRef.current.applyToData;

        // Display state: always discounted
        setData((prevData: any) => {
          const updatedData = { ...prevData, ...mappedData };
          // Build save data with undiscounted when applyToData is false
          const dataToSave = applyToData ? updatedData : { ...prevData, ...undiscountedMappedData };
          // Save staged from inside the updater (so it sees the same prevData the commit
          // rebases on — a sync capture-and-save after setData() silently skips whenever
          // another update landed in the same batch, the inboedel checkmark bug) but
          // FLUSHED after the commit: a setTimeout here would fire twice under
          // StrictMode's double updater invocation and even for discarded renders.
          stageSave(() => saveWorkflowData(screenId, cleanUnselectedCoveragePremiums(dataToSave)));
          return updatedData;
        });
        scheduleSave();
      }

      setCalculationSuccess(true);
      setIsCalculating(false);

      // Clear any address lookup errors on successful calculation
      onCalculationSuccess?.();
    } catch (error) {
      setCalculationError(error instanceof Error ? error.message : 'Calculation failed');
      setLastCalculationOutput({ error: error instanceof Error ? error.message : 'Calculation failed' });
      setIsCalculating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Auto-calculation: Trigger calculation when all required fields are filled
  useEffect(() => {
    // Don't auto-calculate until initial load is complete
    // Also wait for Infofolio property info to complete to avoid duplicate calculations
    // Block auto-calculation if property is a monument (cannot be insured)
    if (!initialLoadComplete.current || loading || isCalculating || isLoadingPropertyInfo || isMonumentProperty || isRentedProperty) {
      return;
    }

    // Skip if we're updating from a calculation result
    if (isUpdatingFromCalculation.current) {
      isUpdatingFromCalculation.current = false;
      return;
    }

    // Clear any existing timer
    if (autoCalculationTimer.current) {
      clearTimeout(autoCalculationTimer.current);
    }

    const ba = data?.Contractdocument?.PP?.BA;
    const ip = data?.Contractdocument?.PP?.IP;
    const ib = data?.Contractdocument?.PP?.IB;

    const inputSignature = inboedelCalcInputSignature(data);

    // When address is locked from Woonhuis, don't require BA_STRAAT/BA_PLAATS for auto-calc
    // (the lookup may still be in progress but the address was validated upstream)
    const addressFieldsReady = isAddressLocked || (ba?.BA_STRAAT && ba?.BA_PLAATS);

    const surfaceAreaReady = isBuildingDetailsLocked || !!ba?.BA_OPPWON;

    // Check if all required fields are filled (Rhion R515 product)
    const hasAllRequiredFields = !!(
      ba?.BA_PCODE &&
      ba?.BA_HUISNR &&
      addressFieldsReady &&
      ba?.BA_TYPEWON &&
      ba?.BA_AARDBW &&
      ba?.BA_AARDDAK &&
      ba?.BA_WONING &&
      ba?.BA_EIGENWO &&
      ba?.BA_PERMBEW &&
      ba?.BA_BDRFBEL &&
      surfaceAreaReady &&
      ip?.IP_GSS &&
      ip?.IP_VERZSOM &&
      ip?.IP_ERB !== undefined &&
      ib?.IB_INBVST
    );

    // Handle initial load auto-calculation trigger
    if (shouldAutoCalculate) {
      setShouldAutoCalculate(false);
      // Skip recalculation if data hasn't changed (e.g., navigating back to page)
      if (inputSignature === lastCalculatedData.current) {
        return;
      }
      if (hasAllRequiredFields) {
        lastCalculatedData.current = inputSignature;
        handleCalculate(data);
      }
      return;
    }

    // Check if input data actually changed
    // Since coverage selections (Glas, Cyber, etc.) are excluded from the signature,
    // toggling them will NOT change the signature and will return early here
    if (inputSignature === lastCalculatedData.current) {
      return;
    }

    // Debounce auto-calculation by 800ms to avoid excessive API calls
    if (hasAllRequiredFields) {
      autoCalculationTimer.current = setTimeout(() => {
        // Validate fields before triggering calculation
        const validationErrors = getValidationErrors();
        // Exclude _calculation and _addressValidation errors from preventing auto-calculation
        // _addressValidation is a hidden check handled by handleCalculate itself
        const { _calculation, _addressValidation, ...fieldErrors } = validationErrors;

        if (Object.keys(fieldErrors).length === 0) {
          lastCalculatedData.current = inputSignature;
          handleCalculate(data);
        } else {
          setValidationErrors(prev => ({ ...prev, ...validationErrors }));
        }
      }, 800);
    }

    // Cleanup timer on unmount
    return () => {
      if (autoCalculationTimer.current) {
        clearTimeout(autoCalculationTimer.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, isCalculating, isLoadingPropertyInfo, isMonumentProperty, shouldAutoCalculate]);

  return {
    isCalculating,
    setIsCalculating,
    calculationError,
    setCalculationError,
    calculationSuccess,
    setCalculationSuccess,
    lastCalculationInput,
    setLastCalculationInput,
    lastCalculationOutput,
    setLastCalculationOutput,
    shouldAutoCalculate,
    setShouldAutoCalculate,
    initialLoadComplete,
    isUpdatingFromCalculation,
    lastCalculatedData,
    handleCalculate,
    previewPrices,
  };
}
